import React, { useState, useEffect, useMemo, useRef } from "react";
import { Col, Dropdown, DropdownMenu, DropdownToggle, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

import SimpleBar from "simplebar-react";
import { DateTime } from "luxon";
import moment from "moment-timezone";

import dummyImg from "../../assets/images/users/user-dummy-img.jpg";

import {
  getVFeesBalance as onGetVFeesBalance,
  getVFeesCompany as onGetVFeesCompany,
  addNewVFee as onAddNewVFee,
  deleteVFee as onDeleteVFee,
  getUsers as onGetUsers,
} from "../../slices/thunks";

const AlertOfBalance = () => {
  const dispatch = useDispatch();

  const [vfeesBalanceList, setVFeesBalanceList] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [total, setTotal] = useState(0);
  const dateRangeRef = useRef([]);

  const handleValidDate = (time) => {
    if (!time) return "";
    return moment(time).tz("Asia/Ulaanbaatar").format("YYYY-MM-DD");
  };

  const handleValidDateTime = (time) => {
    if (!time) return "";
    const parsed = new Date(time);
    if (isNaN(parsed.getTime())) return "";
    return moment.tz(parsed, "Asia/Ulaanbaatar").format("YYYY-MM-DD HH:mm:ss");
  };

  const selectVFeesLayoutState = (state) => state.VFee;
  const vfeesData = createSelector(selectVFeesLayoutState, (state) => ({
    vfeesBalance: state.vfeesBalance,
    isVFeesBalanceSuccess: state.isVFeesBalanceSuccess,
    error: state.error,
    isLoading: state.isLoading,
    vfeesCompany: state.vfeesCompany,
    isVFeesCompanySuccess: state.isVFeesCompanySuccess,
    isVFeesCompanyLoading: state.isVFeesCompanyLoading,
  }));

  const {
    vfeesBalance,
    isVFeesBalanceSuccess,
    vfeesCompany,
    isVFeesCompanySuccess,
    error,
    isLoading,
    isVFeesCompanyLoading,
  } = useSelector(vfeesData);

  const selectLayoutState = (state) => state.Crm;
  const crmuserData = createSelector(selectLayoutState, (state) => ({
    crmusers: state.crmusers,
    isUserSuccess: state.isUserSuccess,
    error: state.error,
  }));

  const { crmusers, isUserSuccess } = useSelector(crmuserData);

  useEffect(() => {
    if (crmusers && !crmusers.length) {
      dispatch(onGetUsers());
    }
  }, [dispatch, crmusers]);

  useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange]);

  useEffect(() => {
    const today = DateTime.now();
    const tenDaysAgo = today.minus({ days: 9 });
    setDateRange([
      handleValidDate(tenDaysAgo.toJSDate()),
      handleValidDate(today.toJSDate()),
    ]);
  }, []);

  useEffect(() => {
    const fetchVFeesBalance = () => {
      const [start, end] = dateRangeRef.current;

      const today = DateTime.now();
      const tenDaysAgo = today.minus({ days: 9 });

      if (start && end) {
        const params = {
          beginDate: handleValidDate(start),
          lastDate: handleValidDate(end),
        };

        dispatch(onGetVFeesBalance(params));
      } else {
        const params = {
          beginDate: handleValidDate(tenDaysAgo.toJSDate()),
          lastDate: handleValidDate(today.toJSDate()),
        };

        dispatch(onGetVFeesBalance(params));
      }
    };

    fetchVFeesBalance();

    const interval = setInterval(() => {
      fetchVFeesBalance();
    }, 90000);

    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    if (
      Array.isArray(vfeesBalance) &&
      vfeesBalance.length > 0 &&
      Array.isArray(crmusers)
    ) {
      const negativeBalances = vfeesBalance
        .filter((item) => item.last_remainder < 0)
        .map((item) => {
          const matchedUser = crmusers.find(
            (user) => user.company === item.company_name
          );

          return {
            ...item,
            phone: matchedUser?.phone || "",
            image_url: matchedUser?.image_url || "",
          };
        })
        .sort((a, b) => a.last_remainder - b.last_remainder);

      const totalNegative = negativeBalances.reduce(
        (sum, item) => sum + item.last_remainder,
        0
      );

      setTotal(totalNegative);

      if (negativeBalances.length > 0) {
        setVFeesBalanceList(negativeBalances);
      }
    }
  }, [vfeesBalance, crmusers]);

  const [isCartDropdown, setIsCartDropdown] = useState(false);

  const toggleCartDropdown = () => {
    setIsCartDropdown(!isCartDropdown);
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={isCartDropdown}
        toggle={toggleCartDropdown}
        className="topbar-head-dropdown ms-1 header-item"
      >
        <DropdownToggle
          type="button"
          tag="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className="ri-calculator-line fs-22"></i>
          <span className="position-absolute cartitem-badge topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
            {vfeesBalanceList.length}
            <span className="visually-hidden">unread messages</span>
          </span>
        </DropdownToggle>
        <DropdownMenu
          className="dropdown-menu-xl dropdown-menu-end p-0 dropdown-menu-cart"
          aria-labelledby="page-header-cart-dropdown"
          style={{ borderRadius: "0 0 14px 14px", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.13)" }}
        >
          <div style={{ background: "linear-gradient(135deg, #f06548 0%, #c0392b 100%)", padding: "16px 18px 14px" }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="ri-secure-payment-line" style={{ color: "#fff", fontSize: 16 }}></i>
                </div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Хасах үлдэгдэлтэй компаниуд</div>
                  <div style={{ color: "rgba(255,255,255,.8)", fontSize: 11 }}>{vfeesBalanceList.length} компани</div>
                </div>
              </div>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 15 }}>
                {vfeesBalanceList.length}
              </div>
            </div>
          </div>
          <SimpleBar style={{ maxHeight: "300px" }}>
            <div className="p-2">
              {vfeesBalanceList.map((item, key) => (
                <div
                  className="d-block dropdown-item text-wrap dropdown-item-cart px-3 py-2"
                  key={key}
                >
                  <div className="d-flex align-items-center">
                    <img
                      src={item.image_url || dummyImg}
                      className="me-3 rounded-circle avatar-sm p-2 bg-light"
                      alt="user-pic"
                      onError={(e) => { e.target.onerror = null; e.target.src = dummyImg; }}
                    />
                    <div className="flex-grow-1">
                      <h6 className="mt-0 mb-1 fs-14">{item.company_name}</h6>
                      <p className="mb-0 fs-12 text-muted">
                        Утас: <span>{item.phone}</span>
                      </p>
                    </div>
                    <div className="px-2">
                      <h6 className="m-0 fw-normal">
                        <span className="cart-item-price text-danger">
                          {item.last_remainder.toLocaleString()} ₮
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SimpleBar>
          <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f4f8", background: "#f8fafc" }} id="checkout-elem">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ fontSize: 13, color: "#6c757d", fontWeight: 500 }}>Нийт үлдэгдэл:</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#f06548" }}>{total.toLocaleString()} ₮</span>
            </div>
            <Link to="/balance" className="btn w-100" style={{ background: "linear-gradient(135deg, #f06548 0%, #c0392b 100%)", color: "#fff", fontWeight: 600, fontSize: 13, borderRadius: 8, padding: "8px 0", border: "none" }}>
              <i className="ri-bank-card-line me-2"></i>Дансны тооцоо
            </Link>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default AlertOfBalance;
